import { useCallback } from 'react';

import { randomUUID } from 'expo-crypto';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { MinimalCartEntryType } from 'features/cart/types';
import { mapLegacyToMinimalCartEntries } from 'features/cart/utils/minimal-cart';
import { useEnableLoyaltyServiceRewards } from 'features/reward/hooks/use-enable-loyalty-service-rewards';
import { useServiceRewards } from 'features/reward/state/service-rewards-context';
import { getReward } from 'features/reward/utils/identity';

import { useCart } from 'hooks/use-cart';
import { actions, useAppDispatch } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';

import { useAppliedRewards } from './use-applied-reward';

/**
 * Increments an already-applied reward by cloning its existing cart entry and linking
 * a new reward slot — the same two steps the PDP add-to-cart button performs, but without
 * requiring navigation back through the product wizard.
 *
 * Handles both cart modes:
 * - Minimal cart (`enableMinimalCartUi = true`): clones the minimal cart entry via
 *   `addItemToMinimalCart` and dispatches `applyReward` manually.
 * - Legacy cart (`enableMinimalCartUi = false`): converts the legacy entry to a minimal
 *   cart entry, sets `rewardBenefitId` so `hydrateEntries` routes it through `handleRewards`,
 *   and calls `hydrateMinimalCart`. This fires the `HydrateCart` mutation the backend needs
 *   for `PriceOrder` to accept the new cart slot, and `handleRewards` dispatches `applyReward`
 *   internally with the preserved `cartId`.
 *
 * Returns early (no-op) if the reward has no cart entry yet, which guards against calling
 * increment before the first application has completed.
 */
export const useIncrementReward = (rewardId: string) => {
  const dispatch = useAppDispatch();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const enableMinimalCartUi = useMinimalCartUi();
  const { enabled: serviceRewardsEnabled } = useEnableLoyaltyServiceRewards();
  const { rewardsMap } = useServiceRewards();

  const appliedRewards = useAppliedRewards();
  const {
    cartEntries: minimalCartEntries,
    addItemToMinimalCart,
    hydrateMinimalCart,
  } = useMinimalCart();
  const { cartEntries: legacyCartEntries } = useCart();

  const handleIncrement = useCallback(async () => {
    // useAppliedRewards is fallback-aware: it reads from the Redux slice when populated, and
    // falls back to the minimal-cart entries for the never-hydrated edge case. IAppliedReward
    // maps sanityId → rewardId, so the lookup here matches the same field IncentiveCard uses
    // to determine isApplied.
    const appliedEntry = appliedRewards.find(r => r.rewardId === rewardId);

    if (!appliedEntry) {
      return;
    }

    const serviceReward = serviceRewardsEnabled
      ? getReward(rewardsMap, appliedEntry.loyaltyEngineId ?? rewardId)
      : undefined;
    if (serviceRewardsEnabled && !serviceReward?.rewardBenefitId) {
      return;
    }

    const newCartId = randomUUID();

    if (enableMinimalCartUi) {
      const sourceEntry = minimalCartEntries.find(e => e.cartId === appliedEntry.cartId);

      if (!sourceEntry) {
        return;
      }

      // appliedEntry.rewardBenefitId is absent on the minimal-cart fallback path (the
      // never-hydrated case), so fall back to the field on the cart entry itself.
      const rewardBenefitId = appliedEntry.rewardBenefitId ?? sourceEntry.rewardBenefitId;
      if (!rewardBenefitId) {
        return;
      }

      addItemToMinimalCart({ ...sourceEntry, cartId: newCartId, isEdit: false });
      dispatch(
        actions.loyalty.applyReward({
          cartId: newCartId,
          quantity: 1,
          ...(serviceReward
            ? {
                reward: {
                  id: serviceReward.id,
                  cmsId: serviceReward.cmsId,
                  rewardBenefitId: serviceReward.rewardBenefitId!,
                  pointCost: serviceReward.pointCost,
                  ...(serviceReward.limitPerOrder !== undefined
                    ? { limitPerOrder: serviceReward.limitPerOrder }
                    : {}),
                },
              }
            : { rewardBenefitId }),
          redemptionMethod: RewardRedemptionMethod.MOBILE,
          enableRewardUnstacking,
          rewardRedemptionLimit,
        })
      );
    } else {
      const sourceEntry = legacyCartEntries.find(e => e.cartId === appliedEntry.cartId);

      if (!sourceEntry) {
        return;
      }

      const rewardBenefitId = appliedEntry.rewardBenefitId;
      if (!rewardBenefitId) {
        return;
      }

      // Convert the legacy entry to minimal cart format, inject reward metadata, then let
      // hydrateMinimalCart fire the HydrateCart mutation. hydrateEntries routes the entry
      // through handleRewards (which dispatches applyReward with the preserved newCartId)
      // and then calls addItemToCart with fromHydration=true to add it to the legacy cart.
      const [minimalEntry] = mapLegacyToMinimalCartEntries([sourceEntry]);
      await hydrateMinimalCart([
        {
          ...minimalEntry,
          cartId: newCartId,
          type: MinimalCartEntryType.reward,
          rewardBenefitId,
          rewardId,
        },
      ]);
    }
  }, [
    appliedRewards,
    rewardId,
    enableMinimalCartUi,
    minimalCartEntries,
    addItemToMinimalCart,
    hydrateMinimalCart,
    legacyCartEntries,
    dispatch,
    enableRewardUnstacking,
    rewardRedemptionLimit,
    rewardsMap,
    serviceRewardsEnabled,
  ]);

  return { handleIncrement };
};
